import { useEffect, useState, useMemo } from 'react'
import useSWRInfinite from 'swr/infinite'
import Link from 'next/link'
import Loader from '@byma/shared/components/Loader'
import Router, { useRouter } from 'next/router'
import { Virtuoso } from 'react-virtuoso'
import dayjs from 'dayjs'
import useAuth from '@byma/shared/hooks/useAuth'
import fetcher from '@byma/shared/helpers/fetcher'
import useFilters from '@byma/shared/hooks/useFilters'
import CouponFilter from '@byma/shared/components/CouponFilter'
import DateFilter from '@byma/shared/components/DateFilter'
import EventFilter from '@byma/shared/components/EventFilter'
import ChannelFilter from '@byma/shared/components/ChannelFilter'
import { methodParser, money } from '@byma/shared/helpers/formatters'
import { getContractValidity, contractStatusLabel, isContractExpired } from '@byma/shared/helpers/contracts'

function getCloudinaryURL(url,height) {
    if (typeof url === 'undefined' || !url) return ''
    return `https://res.cloudinary.com/htkavmx5a/image/upload/c_scale,f_auto,h_${height},q_auto/${url.split('/').pop()}`
}

function toQueryString(obj, forceKey=null) {
    return Object.keys(obj).map(function (key) {
        if (typeof obj[key] === 'object') {
            return toQueryString(obj[key], key)
        }
        return (forceKey || key) + '=' + obj[key]
    }).join('&');
}

export default function ContractsTable() {
    const session = useAuth()
    const { token, user: currentUser } = session
    let { isReady, query, ...router } = useRouter()

    const { originalQueryString, parsedFilters, rawFilters } = useFilters({ ...query, d:'all'}, () => true)

    function getKey(pageIndex, previousPageData) {
        if (previousPageData?.result?.length === 0) return null
        if (pageIndex === 0) {
            return `/api/contracts/getAll?${toQueryString({...parsedFilters, perPage: 50})}`
        }
        const lastResult = previousPageData.result[previousPageData.result.length - 1]
        return `/api/contracts/getAll?${toQueryString({...parsedFilters, toDate: lastResult.created_at, perPage: 50})}`
    }

    const { data: contracts, isValidating, size, setSize } = useSWRInfinite((idx, prvPg) => {
        if (!token) return null
        const key = getKey(idx, prvPg)
        return key ? [key, null, token] : null
    }, fetcher)

    const tableData = useMemo(() => {
        if (!contracts) {
            return []
        }

        return contracts.reduce((acc, cur) => {
            if (!cur?.result) return acc
            return [...acc, ...cur.result]
        }, [])
    }, [contracts])


    function rowRenderer(index, data) {
        return (
            <div
                key={index}
                className="px-3"
            >
                <Link href={`/contracts/${tableData[index]._id}?${originalQueryString}`}>
                    <a>
                        <ContractItem {...{ contract: tableData[index] || {} }} />
                    </a>
                </Link>
            </div>
        )
    }

    return (
        <>
            <Virtuoso
                data={tableData}
                endReached={() => setSize(size+1)}
                overscan={200}
                itemContent={rowRenderer}
                components={{
                    Header: () => {
                        return (
                            <div
                                className="h-2"
                            />
                        )
                    },
                    Footer: () => {
                        return (
                            <div
                                className="p-3 flex items-center justify-center"
                            >
                                { isValidating ? (
                                    <Loader /> 
                                ): (
                                    <p className="text-sm text-gray-700">
                                        { contracts?.result?.length === 0 ? 'Nenhum resultado encontrado' : 'Fim dos resultados' }
                                    </p>
                                )}
                            </div>
                        )
                    }
                }}
            />
        </>
    )
}

function ContractItem ({ contract, active = false }) {
    return (
        <div className={`flex justify-between items-center cursor-pointer box box-hover ${active && 'box-active'}`}>
            <div className="flex items-center min-w-0">

                <div className="min-w-0">
                    <p className="text-md font-bold truncate">
                        {contract.name}
                    </p>
                    <p className="text-sm text-gray-700 truncate">
                        #{contract._id}
                    </p>
                </div>
            </div>
            <div className="flex flex-col text-right min-w-0">
                <div className="flex justify-end items-center space-x-2 mb-1">
                    <p className={`label text-center ${getContractValidity(contract).intent}`}>{getContractValidity(contract).label}</p>
                    <p className="label text-center info">{contractStatusLabel(contract)}</p>
                </div>
                <p className="text-gray-700 text-sm tracking-tight">{isContractExpired(contract) ? 'Expirou em' : 'Expira em'}: {dayjs(contract.expires_at).format('DD/MM/YYYY hh:mm:ss')}</p>
            </div>
        </div>
    )
}
